import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
} from "@mui/material";
import CustomTooltip from "../../shared/components/custom-tooltip/CustomTooltip";
import tooltipIcon from "../../assets/icons/tooltip-icon.svg";
import tooltipIconHover from "../../assets/icons/tooltip-icon-hover.svg";

interface Column {
  key: string;
  label: string;
}

interface AttributesTableProps {
  data: any[];
  columns: Column[];
}

const AttributesTable: React.FC<AttributesTableProps> = ({ data, columns }) => {
  const [hovered, setHovered] = useState(false);

  // Custom widths
  const colWidths: Record<string, string> = {
    Name: "14",
    id: "3%",
    PII: "5%",
    Description: "30%",
    Example: "19%",
    Cardinality: "11%", 
  };

  // Remaining width calculation
  const fixedCols = Object.keys(colWidths);
  const usedWidth = Object.values(colWidths).reduce(
    (acc, val) => acc + parseInt(val.replace("%", "")),
    0
  );
  const remainingWidth = 100 - usedWidth;

  const otherCols = columns.filter((c) => !fixedCols.includes(c.key));
  const otherColWidth =
    otherCols.length > 0 ? `${remainingWidth / otherCols.length}%` : `${remainingWidth}%`;

  return (
    <TableContainer component={Paper} sx={{ width: "100%" }}>
      <Table sx={{ tableLayout: "fixed", width: "100%" }}>
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell
                key={col.key}
                sx={{
                  width: colWidths[col.key] || otherColWidth,
                  whiteSpace: "normal", 
                  wordBreak: "break-word",
                }}
              >
                {col.key === "Cardinality" ? (
                  <CustomTooltip title="The number of unique values present for a specific attribute.">
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      {col.label}
                      <img
                        src={hovered ? tooltipIconHover : tooltipIcon}
                        alt="Tooltip Icon"
                        style={{ cursor: "pointer", width: 16, height: 16 }}
                        onMouseEnter={() => setHovered(true)}
                        onMouseLeave={() => setHovered(false)}
                      />
                    </span>
                  </CustomTooltip>
                ) : (
                  col.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {data.map((row, rowIndex) => (
            <TableRow key={row.id || rowIndex}>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  sx={{
                    width: colWidths[col.key] || otherColWidth,
                    whiteSpace: "normal", 
                    wordBreak: "break-word",
                  }}
                >
                  {row[col.key]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AttributesTable;
